import React, { useRef, useEffect, useState, useMemo, memo } from 'react';
import { Input, Tooltip, Select, Slider, Popover } from 'antd';
import { SearchOutlined, ExclamationCircleOutlined, ZoomInOutlined, ZoomOutOutlined, PlusOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { toTime, toHrsMinsSecs } from '../../libs/formatLib';
import dayjs from '../../dayjs';
import { ShiftBatRowType } from '../../model/shiftBat';
import RouteTitle from '../../components/RouteTitle';
import './GanttView.css';
import { Warning } from "../../components/Icons";
import { Button } from 'react-bootstrap';
import {Link, useHistory} from "react-router-dom";

const HOURS_IN_DAY = 24;
const HOUR_WIDTH = 100; // pixels per hour
const ROW_HEIGHT = 50; // pixels per row
const TIMELINE_WIDTH = HOURS_IN_DAY * HOUR_WIDTH;
const LABEL_WIDTH = 200; // width of duty code label column

const GanttView = ({ shiftBats, onSearch, sortBy, setSortBy, ganttTitle = 'Driver Duty', hasUnsavedChanges = false, showAddDriverDutyButton = true }) => {
  const history = useHistory()
  const headerContainerRef = useRef(null);
  const contentRowsRef = useRef(null);
  const scrollLeftRef = useRef(0);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [hourWidth, setHourWidth] = useState(100); // default zoom
  const [openSubTooltips, setOpenSubTooltips] = useState(new Set());
  const [openParentTooltips, setOpenParentTooltips] = useState(new Set());
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [pendingNavId, setPendingNavId] = useState(null);
  const [openPopoverShiftBatId, setOpenPopoverShiftBatId] = useState(null);
  const [addPopoverOpen, setAddPopoverOpen] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  
  // Handle horizontal scroll synchronization
  useEffect(() => {
    const headerContainer = headerContainerRef.current;
    const contentRows = contentRowsRef.current;
    if (!headerContainer || !contentRows) return;
    const syncScroll = (e) => {
      const target = e.target;
      if (contentRows.contains(target) && target.className === 'gantt-row-timeline-wrapper') {
        headerContainer.scrollLeft = target.scrollLeft;
        scrollLeftRef.current = target.scrollLeft;
        document.querySelectorAll('.gantt-row-timeline-wrapper').forEach(wrapper => {
          if (wrapper !== target) {
            wrapper.scrollLeft = target.scrollLeft;
          }
        });
      }
      if (target === headerContainer) {
        document.querySelectorAll('.gantt-row-timeline-wrapper').forEach(wrapper => {
          wrapper.scrollLeft = target.scrollLeft;
        });
        scrollLeftRef.current = target.scrollLeft;
        checkScrollPosition();
      }
    };
    contentRows.addEventListener('scroll', syncScroll, true);
    headerContainer.addEventListener('scroll', syncScroll);
    // On zoom, re-sync scroll positions
    const syncOnZoom = () => {
      const scrollLeft = scrollLeftRef.current;
      headerContainer.scrollLeft = scrollLeft;
      document.querySelectorAll('.gantt-row-timeline-wrapper').forEach(wrapper => {
        wrapper.scrollLeft = scrollLeft;
      });
      checkScrollPosition();
    };
    syncOnZoom();
    return () => {
      contentRows.removeEventListener('scroll', syncScroll, true);
      headerContainer.removeEventListener('scroll', syncScroll);
    };
  }, [hourWidth, shiftBats.length]);

  // Restore scroll position after tooltip open/close
  useEffect(() => {
    const headerContainer = headerContainerRef.current;
    if (!headerContainer) return;
    const scrollLeft = scrollLeftRef.current;
    headerContainer.scrollLeft = scrollLeft;
    document.querySelectorAll('.gantt-row-timeline-wrapper').forEach(wrapper => {
      wrapper.scrollLeft = scrollLeft;
    });
  }, [openParentTooltips, openSubTooltips]);

  // Generate timeline headers
  const renderTimelineHeaders = () => {
    return Array.from({ length: HOURS_IN_DAY }, (_, i) => (
      <div key={i} className="timeline-header-cell" style={{ width: hourWidth }}>
        {String(i).padStart(2, '0')}:00
      </div>
    ));
  };

  // Convert time string to hours
  const timeToHours = (timeStr) => {
    if (!timeStr) return 0;
    
    // Handle seconds since midnight (most likely scenario)
    if (typeof timeStr === 'number') {
      // Direct conversion from seconds to hours
      return timeStr / 3600;
    }
    
    // Handle cases where we get a dayjs object
    if (typeof timeStr === 'object') {
      if (timeStr._isAMomentObject) {
        return timeStr.hour() + timeStr.minute() / 60;
      }
      // If it's a Date object
      if (timeStr instanceof Date) {
        return timeStr.getHours() + timeStr.getMinutes() / 60;
      }
      // If it's some other object with time info
      if (timeStr.hours !== undefined && timeStr.minutes !== undefined) {
        return timeStr.hours + timeStr.minutes / 60;
      }
      return 0;
    }
    
    // Handle string time format "HH:mm"
    if (typeof timeStr === 'string') {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours + minutes / 60;
    }
    
    return 0;
  };

  // Calculate position and width for trips/breaks
  const calculateItemStyle = (startTime, endTime) => {
    const startHour = timeToHours(startTime);
    const endHour = timeToHours(endTime);
    const left = startHour * hourWidth;
    let width = (endHour - startHour) * hourWidth;
    
    // Handle cases where the duty crosses midnight
    if (endHour < startHour) {
      width = ((24 - startHour) + endHour) * hourWidth;
    }
    
    return {
      left: `${left}px`,
      width: `${Math.max(width, 30)}px` // Minimum width of 30px for visibility
    };
  };

  // Handle sort
  const handleSortChange = (value) => {
    setSortBy(value);
  };

  // Format time in seconds to HH:MM
  const formatSecondsToTime = (seconds) => {
    if (typeof seconds !== 'number') return seconds;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  // Get break type label
  const getBreakTypeLabel = (type) => {
    switch (type) {
      case ShiftBatRowType.break:
        return 'Break';
      case ShiftBatRowType.breakMeal:
        return 'Break Meal';
      case ShiftBatRowType.breakBroken:
        return 'Break Broken';
      default:
        return 'Unknown Break';
    }
  };

  // Get short break label
  const getShortBreakLabel = (type) => {
    switch (type) {
      case ShiftBatRowType.break:
        return 'B';
      case ShiftBatRowType.breakMeal:
        return 'BM';
      case ShiftBatRowType.breakBroken:
        return 'BB';
      default:
        return 'Unknown Break';
    }
  };

  // Detect overlapping items
  const detectOverlaps = (rows) => {
    // Filter to only include types we want to display
    const filteredRows = rows.filter(row => ![
      ShiftBatRowType.noteTimed, 
      ShiftBatRowType.note, 
      ShiftBatRowType.location, 
      ShiftBatRowType.stop, 
      ShiftBatRowType.stopNote, 
      ShiftBatRowType.template
    ].includes(row.type));
    
    // Sort rows by start time
    const sortedRows = [...filteredRows].sort((a, b) => {
      const aTime = a.getTime?.() || 0;
      const bTime = b.getTime?.() || 0;
      return aTime - bTime;
    });
    
    // Mark overlapping rows
    const overlappingRowIds = new Set();
    
    for (let i = 0; i < sortedRows.length; i++) {
      const currentRow = sortedRows[i];
      const currentStartTime = currentRow.getTime?.() || 0;
      const currentEndTime = currentRow.getEndTime?.() || 0;
      
      // Check if this row overlaps with any of the following rows
      for (let j = i + 1; j < sortedRows.length; j++) {
        const nextRow = sortedRows[j];
        const nextStartTime = nextRow.getTime?.() || 0;
        const nextEndTime = nextRow.getEndTime?.() || 0;
        
        // Check for overlap
        if (nextStartTime < currentEndTime) {
          overlappingRowIds.add(currentRow.id);
          overlappingRowIds.add(nextRow.id);
        }
      }
    }
    
    return {
      filteredRows,
      overlappingRowIds
    };
  };

  // Zoom controls
  const handleZoomSlider = value => setHourWidth(value);

  // Navigation functions
  const scrollLeft = () => {
    const headerContainer = headerContainerRef.current;
    if (headerContainer) {
      headerContainer.scrollLeft -= 200; // Scroll by 200px
      checkScrollPosition();
    }
  };

  const scrollRight = () => {
    const headerContainer = headerContainerRef.current;
    if (headerContainer) {
      headerContainer.scrollLeft += 200; // Scroll by 200px
      checkScrollPosition();
    }
  };

  // Check scroll position
  const checkScrollPosition = () => {
    const headerContainer = headerContainerRef.current;
    if (headerContainer) {
      setCanScrollLeft(headerContainer.scrollLeft > 0);
      setCanScrollRight(
        headerContainer.scrollLeft < headerContainer.scrollWidth - headerContainer.clientWidth
      );
    }
  };

  // Handle parent tooltip open/close
  const handleParentTooltipChange = (shiftBatId, open) => {
    setOpenParentTooltips(prev => {
      const newSet = new Set(prev);
      if (open) {
        newSet.add(shiftBatId);
        // Close all sub tooltips when parent opens
        setOpenSubTooltips(new Set());
      } else {
        newSet.delete(shiftBatId);
      }
      return newSet;
    });
  };

  // Handle sub tooltip open/close
  const handleSubTooltipChange = (shiftBatId, rowId, open) => {
    setOpenSubTooltips(prev => {
      const newSet = new Set(prev);
      if (open) {
        newSet.add(`${shiftBatId}-${rowId}`);
        // Close parent tooltip when sub tooltip opens
        setOpenParentTooltips(prevParent => {
          const newParentSet = new Set(prevParent);
          newParentSet.delete(shiftBatId);
          return newParentSet;
        });
      } else {
        newSet.delete(`${shiftBatId}-${rowId}`);
      }
      return newSet;
    });
  };

  // Dynamic height for Gantt
  const rowHeight = 60;
  const minHeight = shiftBats.length > 0 ? shiftBats.length * rowHeight : rowHeight + 0;

  // Memoized GanttRow component
  const GanttRow = memo(function GanttRow({ shiftBat, hourWidth, hasUnsavedChanges, calculateItemStyle, formatSecondsToTime, timeToHours, getBreakTypeLabel, getShortBreakLabel, toHrsMinsSecs, ShiftBatRowType, Warning, RouteTitle, handleParentTooltipChange, openParentTooltips, handleSubTooltipChange, openSubTooltips }) {
    const [openPopover, setOpenPopover] = useState(false);
    const [pendingNav, setPendingNav] = useState(false);

    // Memoize overlap detection
    const { filteredRows, overlappingRowIds } = useMemo(
      () => detectOverlaps(shiftBat.rows || []),
      [shiftBat.rows]
    );
    const hasOverlap = overlappingRowIds.size > 0;

    // Memoize style
    const style = useMemo(() => {
      let startTime = shiftBat.getStartTime?.();
      let endTime = shiftBat.getEndTime?.();
      return calculateItemStyle(startTime, endTime);
    }, [shiftBat, calculateItemStyle]);

    const displayStartTime = formatSecondsToTime(shiftBat.getStartTime?.());
    const displayEndTime = formatSecondsToTime(shiftBat.getEndTime?.());

    // Handler for RouteTitle click
    const handleRouteTitleClick = (e) => {
      e.stopPropagation();
      if (hasUnsavedChanges) {
        setPendingNav(true);
        setOpenPopover(true);
      } else {
        history.push(`/shiftbats/${shiftBat.shiftBatId}`);
      }
    };

    // Handler for popover actions
    const handlePopoverAction = (action) => {
      setOpenPopover(false);
      if (action === 'here' && pendingNav) {
        history.push(`/shiftbats/${shiftBat.shiftBatId}`);
      } else if (action === 'new' && pendingNav) {
        window.open(`/shiftbats/${shiftBat.shiftBatId}`, '_blank');
      }
      setPendingNav(false);
    };

    return (
      <div className="gantt-row">
        <div
          className={`duty-code-cell col-lg-10`}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <Popover
            open={openPopover}
            onOpenChange={open => { if (!open) setOpenPopover(false); }}
            placement="right"
            style={{ zIndex: 1000 }}
            title="Unsaved Changes"
            overlayStyle={{ width: '420px' }}
            content={
              <div>
                <div style={{ marginBottom: 8 }}>
                  <div>You have unsaved changes. Opening this driver duty here may discard them.</div>
                </div>
                <div className='d-flex justify-content-between' style={{ gap: 8 }}>
                  <div className='d-flex' style={{ gap: 8 }}>
                    <Button className="primary-btn secondary-btn btn-small" onClick={() => handlePopoverAction('cancel')}>Cancel</Button>
                    <Button className="primary-btn secondary-btn btn-small" onClick={() => handlePopoverAction('here')}>Open Here</Button>
                  </div>
                  <Button className="primary-btn btn-small" onClick={() => handlePopoverAction('new')}>Open in New Tab</Button>
                </div>
              </div>
            }
          >
            <div
              onClick={handleRouteTitleClick}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <RouteTitle 
                route={{
                  routeNumber: shiftBat.shiftBatNumber,
                  routeName: shiftBat.shiftBatName,
                  routeLogo: shiftBat.shiftBatLogo,
                  colour: shiftBat.shiftBatColour
                }}
              />
              {hasOverlap && (
                <Tooltip title="Validation issues: Overlapping segments">
                  <Warning style={{ height: 14 }} />
                </Tooltip>
              )}
            </div>
          </Popover>
        </div>
        <div className="gantt-row-timeline-wrapper">
          <div className="gantt-row-content" style={{ width: 24 * hourWidth }}>
            <Tooltip
              trigger={['click']}
              getPopupContainer={() => document.querySelector('.gantt-container')}
              open={openParentTooltips.has(shiftBat.shiftBatId)}
              title={
                <div>
                  <div>{shiftBat.shiftBatName || 'Duty'}</div>
                  <div>Start: {displayStartTime}</div>
                  <div>End: {displayEndTime}</div>
                  <div>Duration: {toHrsMinsSecs(shiftBat.getShiftTime?.(), false, true)}</div>
                </div>
              }
              onOpenChange={(open) => {
                handleParentTooltipChange(shiftBat.shiftBatId, open);
              }}
            >
              <div
                className="gantt-item trip"
                style={{
                  ...style, 
                  backgroundColor: shiftBat.shiftBatColour || '#1890ff'
                }}
              >
                <div className="sub-items-container" style={{ 
                  display: 'flex', 
                  flexDirection: 'row', 
                  height: '100%', 
                  position: 'relative',
                  alignItems: 'center',
                }}>
                  {filteredRows.map((row, index) => {
                    const rowStartTime = row.getTime?.();
                    const rowEndTime = row.getEndTime?.();
                    if (!rowStartTime || !rowEndTime) return null;

                    const isBreak = row.type === ShiftBatRowType.break || 
                                   row.type === ShiftBatRowType.breakMeal || 
                                   row.type === ShiftBatRowType.breakBroken;
                    const isOverlapping = overlappingRowIds.has(row.id);

                    const displayRowStartTime = formatSecondsToTime(rowStartTime);
                    const displayRowEndTime = formatSecondsToTime(rowEndTime);

                    // Calculate absolute position
                    const startHour = timeToHours(rowStartTime);
                    const endHour = timeToHours(rowEndTime);

                    // Calculate position as percentage of parent duration
                    const parentStart = timeToHours(shiftBat.getStartTime?.()) || 0;
                    const parentEnd = timeToHours(shiftBat.getEndTime?.()) || 24;
                    const parentDuration = parentEnd - parentStart;

                    const rowStartPercent = ((startHour - parentStart) / parentDuration) * 100;
                    const rowWidthPercent = ((endHour - startHour) / parentDuration) * 100;

                    // Ensure items are visible
                    const visibleStartPercent = Math.max(0, rowStartPercent);
                    const visibleWidthPercent = Math.min(100 - visibleStartPercent, rowWidthPercent);
                    const minWidthPercent = 2;
                    const adjustedWidthPercent = Math.max(visibleWidthPercent, minWidthPercent);

                    return (
                      <Tooltip
                        key={index}
                        trigger={['click']}
                        getPopupContainer={() => document.querySelector('.gantt-container')}
                        open={openSubTooltips.has(`${shiftBat.shiftBatId}-${row.id}`)}
                        title={
                          <div>
                            <div>{row.type === ShiftBatRowType.service ? 
                              `${row.route?.routeNumber || row.route?.routeShortName || ''} - ${row.title || 'Service'}` : 
                              (row.title || (isBreak ? getBreakTypeLabel(row.type) : 'Trip'))}
                            </div>
                            <div>Start: {displayRowStartTime}</div>
                            <div>End: {displayRowEndTime}</div>
                            <div>Duration: {toHrsMinsSecs(row.duration, false, true)}</div>
                            {isOverlapping && <div style={{ color: 'red', fontWeight: 'bold' }}>Overlaps with another item!</div>}
                          </div>
                        }
                        onOpenChange={(open) => {
                          handleSubTooltipChange(shiftBat.shiftBatId, row.id, open);
                        }}
                      >
                        <div
                          className={`gantt-item ${isBreak ? 'break' : 'sub-trip'} ${isOverlapping ? 'overlapping' : ''}`}
                          style={{
                            position: 'absolute',
                            left: `${visibleStartPercent}%`,
                            width: `${adjustedWidthPercent}%`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          {row.type === ShiftBatRowType.service ? (
                            <span className="route-number-badge">
                              {row.route?.routeNumber || row.route?.routeShortName || 'S'}
                            </span>
                          ) : isBreak ? (
                            <span className="break-label">
                              {getShortBreakLabel(row.type)}
                            </span>
                          ) : row.title}
                        </div>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            </Tooltip>
          </div>
        </div>
      </div>
    );
  });

  // Handler for Add Driver Duty button
  const handleAddDutyClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (hasUnsavedChanges) {
      setAddPopoverOpen(true);
    } else {
      history.push('/shiftbats/_new_');
    }
  };

  const handleAddPopoverAction = (action) => {
    setAddPopoverOpen(false);
    if (action === 'here') {
      history.push('/shiftbats/_new_');
    } else if (action === 'new') {
      window.open('/shiftbats/_new_', '_blank');
    }
  };

  return (
    <div className="gantt-view">
      <div className="gantt-controls" style={{ justifyContent: 'space-between', display: 'flex', alignItems: 'center' }}>
        <div>
          {showAddDriverDutyButton && <Popover
            open={addPopoverOpen}
            onOpenChange={open => { if (!open) setAddPopoverOpen(false); }}
            placement="right"
            style={{ zIndex: 1000 }}
            title="Unsaved Changes"
            overlayStyle={{ width: '420px' }}
            content={
              <div>
                <div style={{ marginBottom: 8 }}>
                  <div>You have unsaved changes. Opening this driver duty here may discard them.</div>
                </div>
                <div className='d-flex justify-content-between' style={{ gap: 8 }}>
                  <div className='d-flex' style={{ gap: 8 }}>
                    <Button className="primary-btn secondary-btn btn-small" onClick={() => handleAddPopoverAction('cancel')}>Cancel</Button>
                    <Button className="primary-btn secondary-btn btn-small" onClick={() => handleAddPopoverAction('here')}>Open Here</Button>
                  </div>
                  <Button className="primary-btn btn-small" onClick={() => handleAddPopoverAction('new')}>Open in New Tab</Button>
                </div>
              </div>
            }
          >
            <Button type="primary" className="icon-button" onClick={handleAddDutyClick}><PlusOutlined className="mr-1"/> Add Driver Duty</Button>
          </Popover>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 200 }}>
          {/* Navigation arrows */}
          <div className="gantt-nav-arrows">
            <Tooltip title="Scroll left">
              <div 
                className={`gantt-nav-arrow gantt-nav-left ${!canScrollLeft ? 'disabled' : ''}`}
                onClick={canScrollLeft ? scrollLeft : undefined}
              >
                <LeftOutlined />
              </div>
            </Tooltip>
            <Tooltip title="Scroll right">
              <div 
                className={`gantt-nav-arrow gantt-nav-right ${!canScrollRight ? 'disabled' : ''}`}
                onClick={canScrollRight ? scrollRight : undefined}
              >
                <RightOutlined />
              </div>
            </Tooltip>
          </div>
          <ZoomOutOutlined style={{ color: '#888' }} />
          <Slider
            min={40}
            max={300}
            step={10}
            value={hourWidth}
            onChange={handleZoomSlider}
            style={{ width: 120 }}
            tooltip={{ open: false }}
          />
          <ZoomInOutlined style={{ color: '#888' }} />
        </div>
      </div>
      <div className="gantt-container" style={{ minHeight }}>
        {/* Header row */}
        <div className="header-row">
          <div className="duty-code-header">{ganttTitle}</div>
          <div className="timeline-header-container" ref={headerContainerRef}>
            <div className="timeline-header" style={{ width: HOURS_IN_DAY * hourWidth }}>
              {renderTimelineHeaders()}
            </div>
          </div>
        </div>
        
        {/* Content rows */}
        <div className='scroll-container'>
        <div className="content-rows" ref={contentRowsRef}>
          {shiftBats.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#888', fontSize: 18 }}>
              No driver duties currently utilize this service.
            </div>
          ) : shiftBats.map(shiftBat => (
            <GanttRow
              key={shiftBat.shiftBatId}
              shiftBat={shiftBat}
              hourWidth={hourWidth}
              hasUnsavedChanges={hasUnsavedChanges}
              calculateItemStyle={calculateItemStyle}
              formatSecondsToTime={formatSecondsToTime}
              timeToHours={timeToHours}
              getBreakTypeLabel={getBreakTypeLabel}
              getShortBreakLabel={getShortBreakLabel}
              toHrsMinsSecs={toHrsMinsSecs}
              ShiftBatRowType={ShiftBatRowType}
              Warning={Warning}
              RouteTitle={RouteTitle}
              handleParentTooltipChange={handleParentTooltipChange}
              openParentTooltips={openParentTooltips}
              handleSubTooltipChange={handleSubTooltipChange}
              openSubTooltips={openSubTooltips}
            />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GanttView; 